import { QueryParameters } from '@deck.gl/carto'
import {
  DemoMap,
  Map,
  MapDataset,
  DuplicateMapProps,
  DryRunDuplicateMapResponse,
  SetMapPrivacyRequest,
  Formats,
  RawMapsQueryFilters,
  RawMapListFilters
} from '../types'
import { getRequest, postRequest, patchRequest, deleteRequest, PaginatedResponse } from 'features/common'
import settings from 'settings'
import { uriAddSearchParams } from 'features/common/utils/navigationUtils'
import { getClient } from 'features/common/utils/clientParameter'

export const MAPS_REMOTE_PAGE_SIZE = 12

function baseUrl() {
  const { workspaceApiUrl } = settings
  return `${workspaceApiUrl}/maps`
}

const buildFilterQueryParams = (queryFilters: RawMapsQueryFilters) => {
  const paginationQuery = `page_size=${queryFilters?.pagination?.size || MAPS_REMOTE_PAGE_SIZE}&page=${
    queryFilters?.pagination?.page || 1
  }`
  const orderByQuery = queryFilters?.order
    ? `order_by=${queryFilters.order?.by}&order_direction=${queryFilters.order?.direction}`
    : ''
  const filterQuery = queryFilters?.search ? `search=${queryFilters.search}` : ''
  const tagsQuery = queryFilters?.filter?.tags ? `tags=${JSON.stringify(queryFilters?.filter?.tags)}` : ''
  const ownerQuery = queryFilters?.filter?.owner ? `user_id=${queryFilters?.filter.owner}` : ''
  const privacyQuery = queryFilters?.filter?.privacy ? `privacy=${queryFilters?.filter.privacy}` : ''
  const aiEnabledQuery = queryFilters?.filter?.aiEnabled ? `ai_enabled=${queryFilters?.filter.aiEnabled}` : ''

  return { paginationQuery, orderByQuery, filterQuery, tagsQuery, ownerQuery, privacyQuery, aiEnabledQuery }
}

export const getMapsQuery = ({
  pageParam = 0,
  queryKey: [, queryFilters, pageSize]
}: {
  pageParam?: number
  queryKey: (RawMapListFilters | string | number)[]
}): Promise<PaginatedResponse<Map>> => {
  const {
    search,
    order: { by, direction },
    tags,
    permissions,
    aiEnabled
  } = queryFilters as RawMapListFilters
  const params = new URLSearchParams({
    page: `${pageParam + 1}`,
    page_size: `${pageSize ?? MAPS_REMOTE_PAGE_SIZE}`,
    ...(search && { search }),
    ...(tags?.length && { tags: JSON.stringify(tags) }),
    order_by: by,
    order_direction: direction,
    ...(permissions?.user_id && { user_id: permissions.user_id }),
    ...(permissions?.privacy && { privacy: permissions.privacy as unknown as string }),
    ...(aiEnabled && { ai_enabled: aiEnabled })
  }).toString()

  return getRequest(`${baseUrl()}?${params}`)
}

export const getMaps = async (filters: RawMapsQueryFilters, noPaginate?: boolean): Promise<PaginatedResponse<Map>> => {
  const { paginationQuery, orderByQuery, filterQuery, tagsQuery, ownerQuery, privacyQuery, aiEnabledQuery } =
    buildFilterQueryParams(filters)

  const pagination = noPaginate ? '' : `&${paginationQuery}`
  const orderBy = orderByQuery ? `&${orderByQuery}` : ''
  const withTags = tagsQuery ? `&${tagsQuery}` : ''
  const fromOwner = ownerQuery ? `&${ownerQuery}` : ''
  const withPrivacy = privacyQuery ? `&${privacyQuery}` : ''
  const aiEnabled = aiEnabledQuery ? `&${aiEnabledQuery}` : ''
  const filterQueryParams = [pagination, orderBy, withTags, fromOwner, withPrivacy, aiEnabled].join('')

  const query = `${filterQuery}${filterQueryParams}`
  const maps = await getRequest(`${baseUrl()}?${query}`)
  return maps
}

export const createMap = async (map: Omit<Map, 'id'>): Promise<Map> => {
  const body = cleanUpMapBeforeUpdate(map)
  return await postRequest(baseUrl(), body)
}

export const updateMap = async (map: Partial<Map> & { id: Map['id'] }): Promise<Map> => {
  const body = cleanUpMapBeforeUpdate(map)

  return await patchRequest(`${baseUrl()}/${map.id}`, body)
}

export const getMap = async (mapId: string): Promise<Map> => {
  const uri = uriAddSearchParams(`${baseUrl()}/${mapId}`, { client: getClient() })
  const map = await getRequest(uri)
  delete map.datasets
  return map
}

export const getMapAndDatasets = async (mapId: string): Promise<[mapDatasets: MapDataset[], map: Map]> => {
  return Promise.all([getMapDatasets(mapId), getMap(mapId)])
}

export const getPublicMap = async (
  mapId: string,
  { headers }: { headers?: Record<string, string> } = {}
): Promise<[mapDatasets: Array<MapDataset>, map: Map]> => {
  const { mapsPublicUrl } = settings
  const uri = uriAddSearchParams(`${mapsPublicUrl}/${mapId}`, { client: getClient() })
  const publicMap = await getRequest(uri, { headers })

  const { datasets: mapDatasets, ...map } = publicMap
  // sharingScope is broken in public/viewer versions - just remove it
  // (getMapAllowedUsers will attempt to fetch full map to get correct sharingScope if needed)
  // https://app.shortcut.com/cartoteam/story/485846
  delete map.sharingScope
  return [mapDatasets, map]
}

export const deleteMap = async (mapId: string) => {
  await deleteRequest(`${baseUrl()}/${mapId}`)
}

export const getMapDatasets = async (mapId: string): Promise<Array<MapDataset>> => {
  return await getRequest(`${baseUrl()}/${mapId}/datasets`)
}

export const createMapDataset = async (
  mapId: string,
  mapDataset: Omit<MapDataset, 'id' | 'providerId' | 'connectionName'>
): Promise<MapDataset> => {
  return await postRequest(`${baseUrl()}/${mapId}/datasets`, mapDataset)
}
interface PatchProps {
  mapId: string
  datasetId: string
  body: {
    connectionId?: string
    source?: string
    label?: string
    color?: string
    type?: string
    geoColumn?: string | null
    columns?: string[] | null
    format?: Formats
    aggregationExp?: string | null
    aggregationResLevel?: number | null
    uniqueIdProperty?: string | null
    queryParameters?: QueryParameters | null
    queryTemplate?: string | null
    sourceWorkflowId?: string | null
  }
}

export const patchDataset = async ({ mapId, datasetId, body }: PatchProps): Promise<MapDataset> => {
  return await patchRequest(`${baseUrl()}/${mapId}/datasets/${datasetId}`, body)
}

export const deleteMapDataset = async ({ datasetId, mapId }: { datasetId: string; mapId: string }) => {
  await deleteRequest(`${baseUrl()}/${mapId}/datasets/${datasetId}`)
}

export const setMapPrivacy = async ({ mapId, ...options }: SetMapPrivacyRequest): Promise<unknown> => {
  return await postRequest(`${baseUrl()}/${mapId}/privacy`, options)
}

export const notifySharedUser = async ({ mapId, userId }): Promise<unknown> => {
  return postRequest(`${baseUrl()}/${mapId}/privacy/notify-user`, { userId })
}

const cleanUpMapBeforeUpdate = (map: Omit<Map, 'id'>) => {
  const cleanedMap: Omit<Map, 'id'> = {
    title: map.title,
    description: map.description,
    tags: map.tags,
    keplerMapConfig: map.keplerMapConfig,
    collaborative: map.collaborative,
    agent: map.agent
  }
  return cleanedMap
}

export const dryRunDuplicateMapRequest = async ({ mapId }: DuplicateMapProps): Promise<DryRunDuplicateMapResponse> => {
  return await postRequest(`${baseUrl()}/${mapId}/dry_run_clone`, {})
}

export const duplicateMapRequest = async ({ mapId }: DuplicateMapProps): Promise<Map> => {
  return await postRequest(`${baseUrl()}/${mapId}/clone`, {})
}

export const getDemoMaps = async (aiEnabled = false): Promise<Array<DemoMap>> => {
  const endpoint = `${baseUrl()}/demo/templates`
  const query = new URLSearchParams({ ai_enabled: aiEnabled.toString() }).toString()
  const mapsTemplates = await getRequest(`${endpoint}?${query}`)
  const maps: DemoMap[] = await Promise.all(
    mapsTemplates.map(async (template) => {
      if (template.map) {
        return {
          ...template.map,
          templateUrl: template.publicUrl,
          businessVerticals: template.businessVerticals,
          exists: true
        }
      }
      try {
        const map: Map = await getRequest(`${template.publicUrl}?client=${getClient()}`)

        const demoMap: DemoMap = {
          ...map,
          templateUrl: template.publicUrl,
          businessVerticals: template.businessVerticals,
          exists: false
        }
        return demoMap
      } catch (e) {
        console.error(e)
      }
    })
  )

  return maps
}

export const lockMap = async ({ mapId }: { mapId: string }): Promise<unknown> => {
  return await postRequest(`${baseUrl()}/${mapId}/lock`, {})
}

export const unlockMap = async ({ mapId }: { mapId: string }): Promise<unknown> => {
  return await postRequest(`${baseUrl()}/${mapId}/unlock`, {})
}

export const takeOverMapAction = async ({
  mapId,
  action
}: {
  mapId: string
  action: 'request' | 'reject' | 'accept'
}): Promise<void> => {
  return await postRequest(`${baseUrl()}/${mapId}/take-over/${action}`, {})
}

export const createDemoMap = async (url: string): Promise<Map> => {
  return await postRequest(`${baseUrl()}/demo`, { demoMapUrl: url })
}

export const syncUnlockMapOnUnload = ({ mapId, token }: { mapId: string; token: string }): void => {
  if ('sendBeacon' in navigator) {
    // actually, you can't send headers, so we need to pass token in URL method ...
    navigator.sendBeacon(`${baseUrl()}/${mapId}/unlock?access_token=${token}`)
  } else {
    // attempt old async XMLHttpRequest, but
    try {
      const r = new XMLHttpRequest()
      r.open('POST', `${baseUrl()}/${mapId}/unlock`, false)
      r.setRequestHeader(`Authorization`, `Bearer: ${token}`)
      r.send(null)
    } catch (err) {
      // ignore it, we can't to anythint, we're already unloading
    }
  }
}
